import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { AccessRuleProtocol, SimpleRulesetRule } from "src/api/generated/frontend_internal_v1";
import { useForm, useStore } from "@tanstack/react-form";
import { clsx } from "clsx";
import { Text } from "src/components/base/text";
import { Button } from "src/components/base/button";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/20/solid";
import Form from "src/components/base/form";
import * as Headless from "@headlessui/react";
import { Label, RequiredLabel } from "src/components/base/fieldset";
import { Listbox, ListboxLabel, ListboxOption } from "src/components/base/listbox";
import { Input } from "src/components/base/input";
import { Dialog, DialogActions, DialogDescription, DialogTitle } from "src/components/base/dialog";
import { ApiInternal } from "src/api/api_internal";
import { notify } from "src/components/base/toast";
import { UUID } from "src/api/api_common";

/**
 * The properties for {@link RulesetRules}
 */
export type RulesetRulesProps = {
    /** Callback for changes */
    onChange: () => void;

    /** An existing rule to display */
    rule?: SimpleRulesetRule;

    /** The UUID of the ruleset to edit the rule in */
    ruleset_uuid: UUID;

    /** Is the rule immutable? */
    immutable?: boolean;
};

/**
 * A component to display and edit a ruleset rule
 */
export default function RulesetRules(props: RulesetRulesProps) {
    const [t] = useTranslation("component-rulesets-rules");
    const [tg] = useTranslation();

    const [openDelete, setOpenDelete] = React.useState(false);
    const [editable, setEditable] = React.useState(!props.rule);

    const form = useForm({
        defaultValues: {
            port: props.rule
                ? ["Tcp", "Udp", "Sctp"].includes(props.rule.protocol)
                    ? props.rule.port.toString()
                    : ""
                : "",
            port_range:
                props.rule && props.rule.port_range_end
                    ? ["Tcp", "Udp", "Sctp"].includes(props.rule.protocol)
                        ? props.rule.port_range_end.toString()
                        : ""
                    : "",
            protocol: props.rule ? props.rule.protocol : ("Tcp" as AccessRuleProtocol),
            description: props.rule ? props.rule.description : "",
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (props.rule) {
                    // Update existing Rule
                    await ApiInternal.rulesets.updateRule(props.rule.uuid, {
                        protocol: value.protocol,
                        port: ["Tcp", "Udp", "Sctp"].includes(value.protocol)
                            ? value.port_range !== ""
                                ? { type: "Range", start: parseInt(value.port), end: parseInt(value.port_range) }
                                : {
                                      type: "Single",
                                      port: parseInt(value.port),
                                  }
                            : { type: "Any" },
                        description: value.description,
                    });

                    notify.success(t("toast.rule-updated"));
                    setEditable(false);
                } else {
                    // New Rule
                    await ApiInternal.rulesets.addRule(props.ruleset_uuid, {
                        protocol: value.protocol,
                        port: ["Tcp", "Udp", "Sctp"].includes(value.protocol)
                            ? value.port_range !== ""
                                ? { type: "Range", start: parseInt(value.port), end: parseInt(value.port_range) }
                                : {
                                      type: "Single",
                                      port: parseInt(value.port),
                                  }
                            : { type: "Any" },
                        description: value.description,
                    });

                    notify.success(t("toast.rule-created"));
                    form.reset();
                }
                props.onChange();
            },
        },
    });
    const selectedProtocol = useStore(form.store, ({ values }) => values.protocol);

    /**
     * Delete the given rule
     */
    const deleteRule = async () => {
        if (!props.rule) return;
        await ApiInternal.rulesets.deleteRule(props.rule.uuid);
        props.onChange();
    };

    if (props.immutable || !editable) {
        return (
            <div
                className={clsx(
                    "grid grid-cols-[1fr_8em] gap-6 rounded-lg border px-4 py-2 lg:flex-row lg:gap-12",
                    "bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50",
                )}
            >
                <div className={"grid w-full grid-cols-[1fr_1fr_2fr] gap-x-3 gap-y-1"}>
                    <Text className={"order-1"}>{t("label.protocol")}</Text>
                    <form.Field name={"protocol"}>
                        {(fieldApi) => <Text className={"order-4"}>{fieldApi.state.value.toUpperCase()}</Text>}
                    </form.Field>

                    <Text className={"order-2"}>{t("label.port")}</Text>
                    <form.Field name={"port"}>
                        {(fieldApi) => (
                            <Text className={"order-5"}>
                                {form.state.values.port_range
                                    ? `${fieldApi.state.value} - ${form.state.values.port_range}`
                                    : fieldApi.state.value}
                            </Text>
                        )}
                    </form.Field>

                    <Text className={"order-3"}>{t("label.description")}</Text>
                    <form.Field name={"description"}>
                        {(fieldApi) => <Text className={"order-6"}>{fieldApi.state.value}</Text>}
                    </form.Field>
                </div>

                {props.immutable ? (
                    <div />
                ) : (
                    <Button
                        className={"place-self-end self-center"}
                        aria-label={"edit"}
                        plain={true}
                        onClick={() => setEditable(true)}
                    >
                        <PencilSquareIcon />
                    </Button>
                )}
            </div>
        );
    }

    return (
        <Form onSubmit={form.handleSubmit}>
            <div
                className={clsx(
                    "flex flex-col justify-between gap-6 rounded-lg border p-2 lg:flex-row lg:gap-12",
                    "bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50",
                )}
            >
                <Headless.Fieldset
                    className={
                        "grid w-full grid-cols-[1fr_auto_1fr] gap-x-6 gap-y-2 px-6 py-2 lg:grid-cols-[1fr_1fr_auto_1fr]"
                    }
                >
                    <form.Field name={"protocol"}>
                        {(fieldApi) => (
                            <>
                                <RequiredLabel className={"order-1"}>{t("label.protocol")}</RequiredLabel>
                                <Listbox
                                    className={"order-3 h-fit lg:order-5"}
                                    value={fieldApi.state.value}
                                    onChange={(proto) => fieldApi.handleChange(proto)}
                                >
                                    {Object.keys(AccessRuleProtocol).map((key) => (
                                        <ListboxOption value={key} key={key}>
                                            <ListboxLabel>{key.toUpperCase()}</ListboxLabel>
                                        </ListboxOption>
                                    ))}
                                </Listbox>
                                <div className={"order-2 lg:order-9"}></div>
                            </>
                        )}
                    </form.Field>

                    <form.Field
                        name={"port"}
                        validators={{
                            onChangeListenTo: ["port", "protocol"],
                            onChange: ({ fieldApi, value }) => {
                                if (
                                    (parseInt(value) <= 0 || parseInt(value) >= 65536) &&
                                    ["Tcp", "Udp", "Sctp"].includes(fieldApi.form.getFieldValue("protocol"))
                                ) {
                                    return t("error.port-not-in-range");
                                }

                                return undefined;
                            },
                        }}
                    >
                        {(fieldApi) => (
                            <>
                                <RequiredLabel className={"order-7 lg:order-2"}>{t("label.port")}</RequiredLabel>
                                <Input
                                    className={"order-10 h-fit max-lg:!mt-0 lg:order-6"}
                                    type={"number"}
                                    required={true}
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    disabled={!["Tcp", "Udp", "Sctp"].includes(selectedProtocol)}
                                />
                                <div className={"order-13 lg:order-10"}>
                                    {fieldApi.state.meta.errors.map((err) => (
                                        <Text className={"!text-red-500"}>{err}</Text>
                                    ))}
                                </div>
                            </>
                        )}
                    </form.Field>

                    <span className={"order-8 lg:order-3"}></span>
                    <Text className={"order-11 flex items-center lg:order-7"}>-</Text>
                    <div className={"order-14 lg:order-11"}></div>
                    <form.Subscribe>
                        {(state) => (
                            <form.Field
                                name={"port_range"}
                                validators={{
                                    onChangeListenTo: ["port", "protocol"],
                                    onChange: ({ fieldApi, value }) => {
                                        if (
                                            parseInt(value) <= parseInt(fieldApi.form.getFieldValue("port")) &&
                                            ["Tcp", "Udp", "Sctp"].includes(fieldApi.form.getFieldValue("protocol"))
                                        ) {
                                            return t("error.port-end-too-small");
                                        }
                                        if (
                                            (parseInt(value) <= 0 || parseInt(value) >= 65536) &&
                                            ["Tcp", "Udp", "Sctp"].includes(fieldApi.form.getFieldValue("protocol"))
                                        ) {
                                            return t("error.port-not-in-range");
                                        }

                                        return undefined;
                                    },
                                }}
                            >
                                {(fieldApi) => (
                                    <>
                                        <Label className={"order-9 lg:order-4"}>{t("label.port-range")}</Label>
                                        <Input
                                            className={"order-12 h-fit max-lg:!mt-0 lg:order-8"}
                                            type={"number"}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            disabled={!["Tcp", "Udp", "Sctp"].includes(state.values.protocol)}
                                        />
                                        <div className={"order-15 lg:order-12"}>
                                            {fieldApi.state.meta.errors.map((err) => (
                                                <Text className={"!text-red-500"}>{err}</Text>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </form.Field>
                        )}
                    </form.Subscribe>

                    <form.Field name={"description"}>
                        {(fieldApi) => (
                            <Headless.Field className={"order-16 col-span-3 lg:col-span-4"}>
                                <Label>{t("label.description")}</Label>
                                <Input
                                    className={"mt-2 w-full max-w-lg"}
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                />
                            </Headless.Field>
                        )}
                    </form.Field>
                </Headless.Fieldset>
                <div className={"flex flex-row-reverse justify-start lg:flex-col lg:justify-between"}>
                    {props.rule ? (
                        <Button plain={true} className={"self-end"} onClick={() => setOpenDelete(true)}>
                            <TrashIcon />
                        </Button>
                    ) : (
                        <span></span>
                    )}

                    <div className={"flex gap-2"}>
                        {props.rule && (
                            <Button plain={true} onClick={() => setEditable(false)}>
                                {tg("button.cancel")}
                            </Button>
                        )}
                        <Button className={"self-end"} outline={true} type={"submit"}>
                            {tg("button.save")}
                        </Button>
                    </div>
                </div>
            </div>

            {openDelete && (
                <Suspense>
                    <Dialog open={true} onClose={() => setOpenDelete(false)}>
                        <DialogTitle>{t("heading.delete-rule")}</DialogTitle>
                        <DialogDescription>{t("description.delete-rule")}</DialogDescription>
                        <DialogActions>
                            <Button onClick={() => setOpenDelete(false)}>{tg("button.cancel")}</Button>
                            <Button
                                color={"red"}
                                onClick={async () => {
                                    await deleteRule();
                                    setOpenDelete(false);
                                }}
                            >
                                {t("button.delete-rule")}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Suspense>
            )}
        </Form>
    );
}
